/**
 * Account Query Keys
 *
 * Centralized query key factory for all account-related queries.
 */

/**
 * Account query key factory
 *
 * @example
 * ```typescript
 * // Get law firm users
 * useQuery({ queryKey: accountKeys.users, queryFn: ... })
 * ```
 *
 * Note: there is deliberately no `profile` key here. The signed-in user's
 * `/profile` payload is owned by `profileKeys.user()` and read through the
 * canonical `useProfileQuery` — see `lib/services/profile/hooks.tsx`. A second
 * `['account', 'profile']` key duplicated that fetch on every account mount
 * (LF-3095); do not reintroduce it.
 */
export const accountKeys = {
  /**
   * Root key for all account queries — invalidating this cascades
   * to every other entry in this factory.
   */
  all: ['account'] as const,

  /**
   * Law firm profile (returned by `getLawFirmProfileAction`).
   * Distinct from the user profile (`profileKeys.user()`) — do not collapse.
   */
  lawFirmProfile: ['account', 'firm-profile'] as const,

  /**
   * Law firm users list
   */
  users: ['account', 'users'] as const,

  /**
   * Account/firm statistics
   */
  statistics: ['account', 'statistics'] as const,

  /**
   * Billing pricing structure (returned by `getPricingAction`).
   * Live BE config — refresh on session start, the response itself sets
   * Cache-Control: public, max-age=300 so the browser cache handles freshness.
   */
  billingPricing: ['account', 'billing-pricing'] as const,

  /**
   * Resolved trial-gate / activation-page summary (returned by
   * `getActivationSummaryAction`). Per-firm; the response sets
   * Cache-Control: private, max-age=300.
   */
  billingActivationSummary: ['account', 'billing-activation-summary'] as const,

  /**
   * Firm invoice history list (returned by `getInvoiceHistoryAction`, LF-4604).
   */
  billingInvoices: ['account', 'billing-invoices'] as const,

  /**
   * A single invoice's detail (returned by `getInvoiceDetailAction`, LF-4604).
   * @param invoiceId - The invoice ID
   */
  billingInvoice: (invoiceId: number) =>
    ['account', 'billing-invoice', invoiceId] as const,

  /**
   * Firm's saved payment methods (returned by `getPaymentMethodsAction`, LF-4605).
   */
  billingPaymentMethods: ['account', 'billing-payment-methods'] as const,

  /**
   * Pending invitations list
   */
  invitations: ['account', 'invitations'] as const,

  /**
   * Billing-only "accounts team" contacts list (returned by
   * `getBillingContactsAction`). Separate from `users` / `teamMembers` —
   * billing contacts are not app users and are excluded from the user total.
   */
  billingContacts: ['account', 'billing-contacts'] as const,

  /**
   * User by ID
   * @param userId - The user ID
   */
  user: (userId: number) => [...accountKeys.all, 'user', userId] as const,

  /**
   * Permissions for a user (or current user when omitted)
   * @param userId - The user ID, optional
   */
  permissions: (userId?: number) =>
    [...accountKeys.all, 'permissions', userId] as const,

  /**
   * Every team-members list, whatever its filters — the prefix a cache write
   * has to address (VALE-709).
   *
   * `teamMembers(filters)` is not that prefix, and reaching for it as one is a
   * silent no-op: React Query's partial matcher compares the key element-wise,
   * so `teamMembers()`'s trailing `undefined` fails to match a stored
   * `{ page: 1, limit: 100 }` rather than standing in for it.
   */
  teamMembersRoot: () => [...accountKeys.all, 'team-members'] as const,

  /**
   * Team members list (paginated/filtered)
   * @param filters - Optional filters
   */
  teamMembers: (filters?: object) =>
    [...accountKeys.teamMembersRoot(), filters] as const,

  /**
   * The merge fields authorable in an email signature or letter footer
   * (VALE-445), served from the backend's shared registry.
   *
   * Effectively static — the registry is code-defined and frozen at module
   * load — so consumers set a long `staleTime` rather than refetching it per
   * card mount.
   */
  signatureMergeFields: ['account', 'signature-merge-fields'] as const,

  /**
   * Which active users cannot resolve part of the firm's effective signature
   * (VALE-445). Read by the team page and the onboarding checklist.
   */
  signatureReadiness: ['account', 'signature-readiness'] as const,

  /**
   * The firm's own trust-account change history (VALE-464), read from the
   * existing audit trail. Invalidated by `firm-record-card.tsx`'s trust
   * account save alongside `lawFirmProfile`.
   */
  trustAccountHistory: ['account', 'trust-account-history'] as const,

  /**
   * The firm's offices (VALE-540).
   *
   * Keyed on `includeInactive` because the two reads are genuinely different
   * lists, not one list filtered client-side: the settings card needs closed
   * offices so an admin can reopen them, and every picker must not offer them.
   * Sharing one key would let whichever mounted first decide what the other saw.
   */
  offices: (includeInactive = false) =>
    ['account', 'offices', { includeInactive }] as const,

  /**
   * Every sample-letter render, whatever office it is for — the prefix an
   * invalidation has to address (VALE-1410).
   *
   * Separate from `sampleLetter(officeId)` for the same reason
   * `teamMembersRoot` is separate from `teamMembers`: React Query compares a
   * key element-wise, so `sampleLetter()`'s trailing `{ officeId: undefined }`
   * fails to match a stored `{ officeId: 3 }` rather than standing in for it.
   * An invalidation that reached for the parameterised form would silently
   * refresh nothing.
   */
  sampleLetterRoot: () => ['account', 'sample-letter'] as const,

  /**
   * The rendered sample letter for one office's letterhead
   * (`GET /account/profile/sample-letter`).
   *
   * A **server render of saved state**: Gotenberg turns the firm's letterhead
   * DOCX into a PDF against the acting user's persisted name, position, office
   * and handwritten signature. An unsaved draft cannot be rendered at all,
   * which is why every consumer invalidates this *after* a commit settles and
   * never alongside it.
   *
   * Keyed on the office because the endpoint's `officeId` selects which
   * letterhead prints, so two offices are two different documents rather than
   * one document filtered.
   *
   * @param officeId - The office whose letterhead to render; the firm's
   * default when omitted.
   */
  sampleLetter: (officeId?: number, specimen?: 'institution' | 'client') =>
    [...accountKeys.sampleLetterRoot(), { officeId, specimen }] as const
} as const;
